import { useState } from 'react';
import { User, MapPin, FileText, Shield, Camera, Upload, CheckCircle2, AlertCircle, Edit3, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { COUNTRIES } from '@/lib/index';

export default function DashboardProfile() {
  const { profile, refreshProfile } = useAuth();
  const { t, lang } = useI18n();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const { toast } = useToast();

  const KYC_STATUS_CONFIG = {
    pending: { label: t('kyc_pending'), color: 'bg-warning-light border-warning/20 text-warning', icon: AlertCircle },
    submitted: { label: t('kyc_submitted'), color: 'bg-info-light border-info/20 text-info', icon: AlertCircle },
    verified: { label: t('kyc_verified'), color: 'bg-success-light border-success/20 text-success', icon: CheckCircle2 },
    rejected: { label: t('kyc_rejected'), color: 'bg-danger-light border-danger/20 text-danger', icon: AlertCircle },
  };

  const [form, setForm] = useState({
    first_name: profile?.first_name ?? '',
    last_name: profile?.last_name ?? '',
    phone: profile?.phone ?? '',
    address: profile?.address ?? '',
    city: profile?.city ?? '',
    country: profile?.country ?? 'GB',
    postcode: profile?.postcode ?? '',
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));
  const setPassword = (field: string, value: string) => setPasswordForm((p) => ({ ...p, [field]: value }));

  const saveProfile = async () => {
    if (!profile) return;
    setSaving(true);
    const { error } = await supabase.from('profiles').update(form).eq('id', profile.id);
    if (error) {
      toast({ title: t('error_generic'), description: error.message, variant: 'destructive' });
    } else {
      await refreshProfile();
      setEditing(false);
      toast({ title: t('success_generic'), description: t('profile_edit') });
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({ title: t('error_generic'), description: 'Hasła nie pasują', variant: 'destructive' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast({ title: t('error_generic'), description: 'Hasło musi mieć co najmniej 8 znaków', variant: 'destructive' });
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
    if (error) {
      toast({ title: t('error_generic'), description: error.message, variant: 'destructive' });
    } else {
      toast({ title: t('success_generic'), description: 'Hasło zostało zmienione' });
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    }
    setChangingPassword(false);
  };

  const kycStatus = (profile?.kyc_status ?? 'pending') as keyof typeof KYC_STATUS_CONFIG;
  const kycConfig = KYC_STATUS_CONFIG[kycStatus] ?? KYC_STATUS_CONFIG.pending;
  const KycIcon = kycConfig.icon;

  const initials = profile
    ? `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase() || 'U'
    : 'U';

  const docTypeLabel = profile?.id_document_type === 'passport'
    ? 'Paszport'
    : profile?.id_document_type === 'driving_license'
      ? 'Prawo jazdy'
      : profile?.id_document_type === 'national_id'
        ? 'Dowód osobisty'
        : '—';

  const kycExplanation = {
    pending: 'Twoje konto oczekuje na weryfikację. Prześlij dokumenty, aby rozpocząć proces.',
    submitted: 'Twoje dokumenty są weryfikowane. Zwykle trwa to 24-48 godzin.',
    verified: 'Twoje konto zostało zweryfikowane. Masz pełny dostęp do wszystkich funkcji.',
    rejected: 'Weryfikacja została odrzucona. Skontaktuj się z obsługą klienta.',
  };

  return (
    <DashboardLayout title={t('profile_title')}>
      <div className="max-w-4xl mx-auto space-y-6 p-6">
        <Card className="border-border/50 overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-card to-muted/30 relative">
            <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 30% 50%, color-mix(in oklch, var(--gold) 40%, transparent) 0%, transparent 70%)' }} />
          </div>
          <CardContent className="p-6 -mt-12">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <Avatar className="w-24 h-24 border-4 border-card text-3xl">
                <AvatarFallback className="bg-gold text-navy text-2xl font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-foreground">
                  {profile ? `${profile.first_name} ${profile.last_name}` : 'Użytkownik'}
                </h2>
                <p className="text-muted-foreground text-sm">{profile?.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="capitalize bg-gold/10 text-gold border-gold/30">
                    {t(`plan_${profile?.plan.toLowerCase() ?? 'standard'}`)}
                  </Badge>
                  <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border ${kycConfig.color}`}>
                    <KycIcon className="w-3 h-3" />
                    KYC: {kycConfig.label}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-gold" /> Dane osobowe
                </CardTitle>
                {!editing ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="gap-1 hover:text-gold">
                    <Edit3 className="w-3 h-3" /> {t('profile_edit')}
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={saveProfile} disabled={saving} className="gap-1 text-success hover:text-success/80">
                      <Save className="w-3 h-3" /> {saving ? t('loading') : t('btn_save')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setForm({ first_name: profile?.first_name ?? '', last_name: profile?.last_name ?? '', phone: profile?.phone ?? '', address: profile?.address ?? '', city: profile?.city ?? '', country: profile?.country ?? 'GB', postcode: profile?.postcode ?? '' }); }} className="gap-1 text-muted-foreground">
                      <X className="w-3 h-3" /> {t('btn_cancel')}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Imię</Label>
                      <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="h-10 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Nazwisko</Label>
                      <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="h-10 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Telefon</Label>
                      <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44 7700 000000" className="h-10 text-sm" />
                    </div>
                  </>
                ) : (
                  <>
                    {[
                      { label: 'Imię', value: profile?.first_name },
                      { label: 'Nazwisko', value: profile?.last_name },
                      { label: 'Email', value: profile?.email },
                      { label: 'Telefon', value: profile?.phone },
                      { label: 'Data urodzenia', value: profile?.date_of_birth ?? '—' },
                    ].map((item) => (
                      <div key={item.label}>
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className="text-sm font-medium text-foreground">{item.value || '—'}</p>
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" /> Adres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Adres</Label>
                      <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="12 Example Street" className="h-10 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Miasto</Label>
                        <Input value={form.city} onChange={(e) => set('city', e.target.value)} className="h-10 text-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Kod pocztowy</Label>
                        <Input value={form.postcode} onChange={(e) => set('postcode', e.target.value)} className="h-10 text-sm font-mono" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Kraj</Label>
                      <Select value={form.country} onValueChange={(value) => set('country', value)}>
                        <SelectTrigger className="h-10 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    {[
                      { label: 'Adres', value: profile?.address },
                      { label: 'Miasto', value: profile?.city },
                      { label: 'Kod pocztowy', value: profile?.postcode },
                      { label: 'Kraj', value: COUNTRIES.find((c) => c.code === profile?.country)?.name || profile?.country },
                    ].map((item, idx) => (
                      <div key={`${item.label}-${idx}`}>
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className="text-sm font-medium text-foreground">{item.value || '—'}</p>
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gold" /> Weryfikacja KYC
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`p-4 rounded-xl border ${kycConfig.color}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <KycIcon className="w-5 h-5" />
                    <div>
                      <p className="text-sm font-semibold">Status: {kycConfig.label}</p>
                      <p className="text-xs opacity-80 mt-1">{kycExplanation[kycStatus]}</p>
                    </div>
                  </div>
                </div>

                {[
                  { label: 'Typ dokumentu', value: docTypeLabel },
                ].map((item, idx) => (
                  <div key={`${item.label}-${idx}`}>
                    <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                    <p className="text-sm font-medium text-foreground font-mono">{item.value}</p>
                  </div>
                ))}

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Dokument tożsamości</p>
                    <div className={`h-16 rounded-lg border flex items-center justify-center ${profile?.id_document_front_url ? 'border-success/30 bg-success-light' : 'border-border bg-muted/30'}`}>
                      {profile?.id_document_front_url ? (
                        <p className="text-xs text-success flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Przesłano</p>
                      ) : (
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Upload className="w-3 h-3" /> Brak</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Selfie</p>
                    <div className={`h-16 rounded-lg border flex items-center justify-center ${profile?.selfie_url ? 'border-success/30 bg-success-light' : 'border-border bg-muted/30'}`}>
                      {profile?.selfie_url ? (
                        <p className="text-xs text-success flex items-center gap-1"><Camera className="w-3 h-3" /> Przesłano</p>
                      ) : (
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Camera className="w-3 h-3" /> Brak</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gold" /> Zmiana hasła
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nowe hasło</Label>
                    <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPassword('newPassword', e.target.value)} placeholder="Minimum 8 znaków" className="h-10 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Potwierdź hasło</Label>
                    <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPassword('confirmPassword', e.target.value)} className="h-10 text-sm" />
                  </div>
                  <Button onClick={changePassword} disabled={changingPassword || !passwordForm.newPassword || !passwordForm.confirmPassword} className="w-full bg-gold hover:bg-gold-dim text-navy">
                    {changingPassword ? t('loading') : 'Zmień hasło'}
                  </Button>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/30 border border-border rounded-xl">
                  <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">Twoje hasło jest szyfrowane i przechowywane bezpiecznie. Nigdy nie udostępniaj go nikomu.</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <Card className="border-border/50 bg-muted/20">
          <CardContent className="p-4 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Konto utworzone:{' '}
              <span className="text-foreground font-medium">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">ID: <span className="font-mono">{profile?.id?.slice(0, 8).toUpperCase() ?? '—'}</span></p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
